import { ThemeNames } from '@tenants/types';
import { CountryCodeList, type CountryCode } from 'types/countries';
import { defaultHomeType } from 'constants/listing-components';
import {
  AVAILABLE_STATUS,
  NOT_AVAILABLE_SOLD_STATUS,
  type Status,
} from 'contexts/preferences/listing-params/types';
import replaceFrenchAccents from 'utils/replace-french-accents';
import { fetchHomePageListings } from '../../fetcher';
import { AreaPageListingCardData } from 'components/pages/area-listings/area_page_listing_card_data';
import { parseCityAndProvince } from '../homepage-local-focus/derivation';
import {
  provinceOrStateNameFromCode,
  provinceSlugMappings,
  stateSlugMappings,
  type CanadaProvinceCode,
  type ProvinceOrStateCode,
  type UsaStateCode,
} from 'utils/province_or_state';

export type ZoocasaLightAreaListings = {
  cardRows: AreaPageListingCardData[];
  totalCount: number;
  isExpandedArea: boolean;
  slug: string;
  label: string;
};

type AreaListingsFetchParams = {
  tenantThemeId: ThemeNames;
  isCanadianSite: boolean;
  slug: string;
  areaLabel: string;
  status: Status;
  pageSize: number;
  hasImage: boolean;
};

/**
 * Fetch Listings to show on zoocasa light homepage sections/ carousels
 */
export async function fetchZoocasaLightHomeListings(
  params: AreaListingsFetchParams
): Promise<ZoocasaLightAreaListings> {
  const { tenantThemeId, isCanadianSite, slug, areaLabel, status, pageSize, hasImage } = params;
  const country: CountryCode = isCanadianSite ? CountryCodeList.CANADA : CountryCodeList.UNITED_STATES;
  const queryBase = {
    country,
    filter: {
      status,
      homeType: defaultHomeType,
      hasImage,
      tenant: tenantThemeId,
    },
    pageSize,
    isClient: true as const,
    useLegacySearchFilter: false as const,
  };

  const { listings, totalCount, didFallback, isExpandedArea } = await fetchHomePageListings({
    ...queryBase,
    slug,
  });
  const { city: requestedCity, province: requestedProvince } = parseCityAndProvince(areaLabel, slug);
  const normalizedCity = replaceFrenchAccents(requestedCity.toLowerCase().trim());
  const hasLocalListings = normalizedCity
    ? listings.some(l => replaceFrenchAccents((l.subDivision ?? '').toLowerCase().trim()) === normalizedCity)
    : true;
  const shouldFallback = (didFallback || !hasLocalListings) && slug;

  // If there are no listings for that city or slug, fallback to province and state level listings
  if (shouldFallback) {
    const code = requestedProvince.toLowerCase();
    const provinceName = provinceOrStateNameFromCode(code as ProvinceOrStateCode);
    const provinceSlug = isCanadianSite
      ? provinceSlugMappings[code as CanadaProvinceCode]
      : stateSlugMappings[code as UsaStateCode];
    if (provinceName && provinceSlug) {
      const provinceResult = await fetchHomePageListings({
        ...queryBase,
        slug: provinceSlug,
      });
      return {
        cardRows: provinceResult.listings.map(l => new AreaPageListingCardData(l)),
        totalCount: provinceResult.totalCount,
        isExpandedArea: provinceResult.isExpandedArea ?? false,
        slug: provinceSlug,
        label: provinceName,
      };
    }
  }

  return {
    cardRows: listings.map(l => new AreaPageListingCardData(l)),
    totalCount,
    isExpandedArea,
    slug: slug,
    label: areaLabel,
  };
}

// Fetch for Sold Listings carousel 
export async function fetchForSaleAreaListings(params: {
  tenantThemeId: ThemeNames;
  isCanadianSite: boolean;
  slug: string;
  areaLabel: string;
}): Promise<ZoocasaLightAreaListings> {
  return fetchZoocasaLightHomeListings({
    ...params,
    status: AVAILABLE_STATUS as Status,
    pageSize: 15,
    hasImage: true,
  });
}

// Fetch for Sold Listings carousel 
export async function fetchSoldListingsForArea(params: {
  tenantThemeId: ThemeNames;
  isCanadianSite: boolean;
  slug: string;
  areaLabel: string;
}): Promise<ZoocasaLightAreaListings> {
  return fetchZoocasaLightHomeListings({
    ...params,
    status: NOT_AVAILABLE_SOLD_STATUS as Status,
    pageSize: 10,
    hasImage: params.tenantThemeId !== ThemeNames.ZOOCASA,
  });
}

// Fetch for Featured Listings carousel, only for Canada 
export async function fetchFeaturedListingsForArea(params: {
  tenantThemeId: ThemeNames;
  isCanadianSite: boolean;
  slug: string;
  areaLabel: string;
}): Promise<ZoocasaLightAreaListings> {
  return fetchZoocasaLightHomeListings({
    ...params,
    status: AVAILABLE_STATUS as Status,
    pageSize: 10,
    hasImage: true,
  });
}
